import React, { useId, useState, useEffect, useContext } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import Icon from "../icon"
import ButtonGroup, { ButtonGroupContext } from "./button-group"
import IconButton from "./icon-button"
import "./button-styles.css"
import { emit, EVENTS, useTelemetry } from "../event-emitter"
import { extractText } from "../utils"

const SIZES = {
  sm: "text-sm py-1 px-3 h-8 rounded-sm",
  md: "text-sm py-2 px-4 h-10 rounded",
  lg: "text-sm py-3 px-5 h-12 rounded-lg"
}

const GROUP_SIZES = {
  "horizontal-sm": "h-full px-2 rounded-xs",
  "horizontal-md": "h-full px-3 rounded-sm",
  "horizontal-lg": "h-full px-4 rounded",
  "vertical-sm": "p-1 w-full rounded-xs",
  "vertical-md": "p-1 w-full rounded-sm",
  "vertical-lg": "p-2 w-full rounded"
}

export const TOKENS = {
  primary: "text-white hover:text-white bg-black hover:bg-gray-800",
  "primary-blue":
    "text-gray-900 hover:text-black bg-blue-500 hover:bg-blue-400",
  secondary: "text-gray-900 bg-gray-900/[0.06] hover:bg-gray-900/[0.12]",
  tertiary: "text-gray-900 bg-transparent hover:bg-gray-900/[0.06]",
  plain: ""
}

const DISABLED_CLASSES = "text-gray-600 bg-gray-100"
const DISABLED_TERTIARY_CLASSES = "text-gray-600 bg-transparent"

const COLOR_FALLBACK = {
  red: TOKENS["primary"],
  white: TOKENS["secondary"],
  plain: TOKENS["tertiary"],
  gray: TOKENS["secondary"],
  yellow: "text-white bg-yellow-500 hover:bg-yellow-600",
  blue: "text-gray-900 bg-blue-500 hover:bg-blue-400 hover:text-black focus:text-gray-900",
  green: "text-white bg-green-500 hover:bg-green-600",
  "light-blue": "text-gray-900 bg-blue-200 hover:bg-blue-300",
  "light-green":
    "text-gray-900 bg-green-200 hover:bg-green-300 focus:bg-green-300",
  "light-yellow": "text-gray-900 bg-yellow-200 hover:bg-yellow-300",
  "light-red": "text-gray-900 bg-red-200 hover:bg-red-300"
}

/**
 * A versatile button component that supports multiple style variants, sizes, and states.
 * Can render as a button or anchor element depending on whether an href is provided.
 */
const Button = React.forwardRef(
  (
    {
      is,
      color,
      size = "md",
      icon,
      disabled = false,
      className,
      children,
      onMouseDown = () => {},
      onBlur = () => {},
      onClick,
      active,
      telemetry,
      ...props
    },
    ref
  ) => {
    const id = useId()
    const groupContext = useContext(ButtonGroupContext)
    const telemetryPayload = useTelemetry(telemetry)
    const [clicked, setClicked] = useState(false)
    const DEFAULT = `leading-6 border border-solid border-transparent no-underline font-sans font-semibold transition duration-200 hover:no-underline focus:no-underline focus:outline-none align-middle text-center inline-flex items-center justify-center`
    const isDisabled =
      disabled &&
      (Object.keys(TOKENS).includes(is) || color || groupContext.present)
    const usesTokenStyles =
      Object.keys(TOKENS).includes(is) || isDisabled || color
    const isCurrentActiveGroupMember = groupContext.activeId === id || null
    const classes = cx(
      className,
      disabled ? "cursor-default" : "cursor-pointer",
      {
        [DEFAULT]: usesTokenStyles || groupContext.present,
        [SIZES[size]]: usesTokenStyles && !groupContext.present,
        [COLOR_FALLBACK[color]]: color && !isDisabled && !groupContext.present,
        [TOKENS[is]]: is && !isDisabled && !groupContext.present,
        [DISABLED_CLASSES]: isDisabled && is !== "tertiary",
        [DISABLED_TERTIARY_CLASSES]: isDisabled && is === "tertiary",
        "focus:outline-blue": !clicked && !groupContext.present,
        [GROUP_SIZES[groupContext.groupSize]]:
          groupContext.present && size !== "icon",
        "h-full aspect-square": groupContext.present && size === "icon",
        "hover:bg-gray-900/[0.06]":
          groupContext.present && !isDisabled && !isCurrentActiveGroupMember,
        "bg-white": isCurrentActiveGroupMember
      }
    )

    // Set active id in group context
    const { setActiveId } = groupContext
    useEffect(() => {
      if (active && setActiveId) {
        setActiveId(id)
      }
    }, [active, setActiveId, id])

    const handleMouseDown = event => {
      setClicked(true)
      onMouseDown(event)
    }

    const handleBlur = event => {
      setClicked(false)
      onBlur(event)
    }

    const handleClick = event => {
      if (setActiveId) {
        setActiveId(id)
      }

      if (telemetryPayload !== false) {
        emit(EVENTS.BUTTON_CLICK, {
          text: extractText(children),
          ...telemetryPayload
        })
      }

      if (onClick) onClick(event)
    }

    const Component = props.href ? "a" : "button"

    return (
      <Component
        {...props}
        ref={ref}
        className={classes}
        disabled={disabled}
        onMouseDown={handleMouseDown}
        onBlur={handleBlur}
        onClick={handleClick}
        aria-pressed={isCurrentActiveGroupMember}
      >
        {icon && (
          <Icon
            name={icon}
            className={cx("-ml-1 pointer-events-none", {
              "mr-2": Boolean(children)
            })}
            aria-hidden={children ? "true" : "false"}
          />
        )}
        {children}
      </Component>
    )
  }
)

Button.propTypes = {
  /** HTML button type attribute */
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  /** Custom CSS classes to apply to the button */
  className: PropTypes.string,
  /** Button content */
  children: PropTypes.node,
  /** Style variant of the button */
  is: PropTypes.oneOf([
    "primary", // main color
    "primary-blue", // blue (for use post-rebrand)
    "secondary", // white
    "tertiary", // transparent with bg on hover
    "plain" // sets defaults without color for easy customization
  ]),
  /** Temporary fallback for "is" prop */
  color: PropTypes.oneOf(["red", "white", "plain"]),
  /** Whether the button is disabled */
  disabled: PropTypes.bool,
  /** Size of the button: "sm", "md", or "lg" */
  size: PropTypes.oneOf(Object.keys(SIZES)),
  /** Icon to display before children */
  icon: PropTypes.elementType,
  /** If provided, renders as an anchor element instead of a button */
  href: PropTypes.string,
  /** Used to manage active state in button group */
  active: PropTypes.bool,
  /** Handler called when mouse button is pressed down */
  onMouseDown: PropTypes.func,
  /** Handler called when button loses focus */
  onBlur: PropTypes.func,
  /** Handler called when button is clicked */
  onClick: PropTypes.func,
  /** Data to emit alongside the "button_click" event. Set to `false` to disable emission. */
  telemetry: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([false])])
}

// eslint-disable-next-line import/no-default-export
export default Button

export { ButtonGroup, IconButton }
